import * as React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import { Receipt as ReceiptIcon } from "@mui/icons-material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { brand, text, neutral, surface, status } from "../../theme";

export default function BusinessOverviewChart({
  monthlyData = [],
  monthName = "",
}) {
  const isEmpty = !monthlyData || monthlyData.length === 0;

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${neutral[200]}`,
        borderRadius: "12px",
        p: 1.5,
        height: 250,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "5px",
              bgcolor: brand[100],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ReceiptIcon sx={{ fontSize: 13, color: brand.main }} />
          </Box>
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.82rem", color: text.primary }}
          >
            {monthName
              ? `Business Overview — ${monthName}`
              : "Business Overview"}
          </Typography>
        </Stack>
      </Stack>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {isEmpty ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: text.muted,
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            No data available
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={monthlyData}
              margin={{ top: 5, right: 8, left: -18, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorLR" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={brand.light}
                    stopOpacity={0.35}
                  />
                  <stop offset="95%" stopColor={brand.light} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorBills" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={status.info.main}
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="95%"
                    stopColor={status.info.main}
                    stopOpacity={0}
                  />
                </linearGradient>
                <linearGradient id="colorReceived" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={status.success.main}
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="95%"
                    stopColor={status.success.main}
                    stopOpacity={0}
                  />
                </linearGradient>
                <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={status.warning.main}
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="95%"
                    stopColor={status.warning.main}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={surface.borderLight}
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 10, fill: text.secondary }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: text.secondary }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v
                }
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 6,
                  border: `1px solid ${neutral[200]}`,
                  fontSize: 11,
                  padding: "4px 8px",
                }}
              />
              <Legend
                iconType="circle"
                iconSize={7}
                wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
              />
              <Area
                type="monotone"
                dataKey="totalLR"
                name="LR"
                stroke={brand.light}
                fill="url(#colorLR)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="totalBills"
                name="Bills"
                stroke={status.info.main}
                fill="url(#colorBills)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="received"
                name="Received"
                stroke={status.success.main}
                fill="url(#colorReceived)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="pending"
                name="Pending"
                stroke={status.warning.main}
                fill="url(#colorPending)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
}
